import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import FakeLink from "@/components/FakeLink/FakeLink";
import MoreButton from "@/components/MoreButton/MoreButton";
import PageButtonGroup from "@/components/PageButtonGroup/PageButtonGroup";
import { PageHeaderNew } from "@/components/PageHeader/PageHeaderNew";
import StatusTag from "@/components/StatusTag/StatusTag";
import StatusTags from "@/components/StatusTags/StatusTags";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import Table, { Column } from "@/components/Table/Table";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import useTranslation from "@/hooks/useTranslation";
import useUrlStateBoolean from "@/hooks/useUrlStateBoolean";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import styles from "@/pages/products/bom/BillOfMaterials.module.scss";
import UsageModal from "@/pages/products/usage/usageModal";
import { ProductUsageOverview, useGetProductUsageOverview } from "@/pages/products/usage/useUsage";
import { useProductUsageStatusPerYear } from "@/pages/products/useProducts";
import { DatabaseOutlined, EditOutlined, MoreOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Space, Tooltip } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PRODUCTS_BREADCRUMBS } from "../breadcrumbs";
import HiddenProductsMessage from "../HiddenProductsMessage";

function UsageOverview() {
  useTrackOnLoad("Usage Overview Viewed");
  const translation = useTranslation();
  const navigate = useNavigate();
  const { year } = useDashboardYear();
  const [selectedProducts, setSelectedProducts] = useState<ProductUsageOverview[]>([]); // TODO: add type
  const { data: productsUsageYears } = useProductUsageStatusPerYear();
  const [showUnrelated, setShowUnrelated] = useUrlStateBoolean("showUnrelated", false);

  const columns: Column<ProductUsageOverview>[] = [
    {
      key: "external_id",
      label: "ID",
      searchable: true,
      sortable: true,
      minWidth: "115px",
      maxWidth: "200px",
    },
    {
      key: "name",
      label: "Product",
      searchable: true,
      sortable: true,
      minWidth: "200px",
      maxWidth: "300px",
    },
    {
      key: "category",
      label: "Category",
      sortable: true,
      searchable: true,
      minWidth: "165px",
      renderer: (product) => product.category || "-",
    },
    {
      key: "lifespan",
      label: "Lifespan",
      minWidth: "90px",
      maxWidth: "100px",
      renderer: (product) => product.lifespan || "-",
    },
    {
      key: "electricity_amount",
      label: "Utilities",
      minWidth: "100px",
      maxWidth: "125px",
      renderer: (product) =>
        product.utilities_used === null
          ? "-"
          : product.utilities_used && !product.electricity_amount
            ? "yes"
            : product.utilities_used && product.electricity_amount
              ? `${product.electricity_amount} kWh`
              : "no",
    },
    {
      key: "consumables_count",
      label: "Consumables",
      maxWidth: "120px",
      renderer: (product) =>
        product.consumables_used === null
          ? "-"
          : product.consumables_used && !product.consumables_count
            ? "yes"
            : product.consumables_used && product.consumables_count
              ? product.consumables_count
              : "no",
    },
    {
      key: "status",
      label: "Status",
      minWidth: "150px",
      maxWidth: "150px",
      sortable: true,
      renderer: (product) => (
        <StatusTag
          status={product.status}
          showIcon={false}
        />
      ),
      filterable: true,
    },
    {
      key: "actions",
      label: "Actions",
      noLink: true,
      minWidth: "80px",
      maxWidth: "80px",
      renderer: (product) => (
        <ActionsPopover
          actions={
            <>
              <UsageModal
                selectedProducts={[product]}
                onSuccess={() => setSelectedProducts([])}
              >
                <ActionsPopOverItem icon={<EditOutlined />}>Edit Usage</ActionsPopOverItem>
              </UsageModal>
            </>
          }
        >
          <MoreOutlined />
        </ActionsPopover>
      ),
      align: "end",
    },
  ];

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "external_id", order: "ascend" },
    });

  const onSelectedKeysChange = (keys: number[]) => {
    // analytics.track("Product Selected"); TODO add analytics
    setSelectedProducts((currentProducts) => {
      let productsOnOtherPages = [
        ...currentProducts.filter((x) => !data?.data?.map((x) => x.id).includes(x.id)),
      ];
      return [
        ...productsOnOtherPages,
        ...(data?.data?.filter((product) => keys.includes(product.id)) || []),
      ];
    });
  };

  const { data, isLoading, isFetching } = useGetProductUsageOverview({
    pagination,
    filter,
    search,
    sort,
    year,
    showUnrelated,
    enabled: !!year,
  });

  const resetTable = () => {
    setPage(1);
    setSelectedProducts([]);
  };

  return (
    <div>
      <PageHeaderNew
        title={translation.usage.overview.pageTitle}
        description={translation.usage.overview.pageDescription}
        breadcrumbs={PRODUCTS_BREADCRUMBS}
        titleRight={
          <Space.Compact>
            <Button
              onClick={() => navigate("/consumables")}
              icon={<DatabaseOutlined />}
            >
              Usage consumables
            </Button>
            <MoreButton
              actions={
                <ActionsPopOverItem
                  to="/impact_references"
                  icon={<SettingOutlined />}
                >
                  Manage impact references
                </ActionsPopOverItem>
              }
            />
          </Space.Compact>
        }
      />
      <TableHeader
        noRoundedTop
        left={
          <>
            {selectedProducts.length > 0 ? (
              <span className={styles["num-selected"]}>{selectedProducts.length} selected:</span>
            ) : (
              <YearSelector
                disableYears={showUnrelated}
                optionData={productsUsageYears}
                onChange={resetTable}
                right={
                  <Tooltip
                    placement="bottom"
                    title="Show products that are not related to purchase or sales orders"
                  >
                    <>
                      <SwitchWithLabel
                        togglePosition="right"
                        size="small"
                        checked={showUnrelated}
                        onChange={(disabled) => {
                          resetTable();
                          setShowUnrelated(disabled);
                        }}
                      >
                        Year unrelated
                      </SwitchWithLabel>
                    </>
                  </Tooltip>
                }
              />
            )}
            {selectedProducts.length > 0 && (
              <PageButtonGroup loading={isLoading}>
                <UsageModal
                  selectedProducts={selectedProducts}
                  onSuccess={() => setSelectedProducts([])}
                >
                  <Button icon={<EditOutlined />}>Edit usage</Button>
                </UsageModal>
              </PageButtonGroup>
            )}
            {selectedProducts.length > 0 && (
              <FakeLink onClick={() => setSelectedProducts([])}>Clear selection</FakeLink>
            )}
            <HiddenProductsMessage />
          </>
        }
        right={
          <StatusTags
            data={data}
            loading={isLoading}
          />
        }
      />
      <Table<ProductUsageOverview>
        availableFilters={{
          status: [
            { label: "Complete", value: "complete" },
            { label: "Incomplete", value: "incomplete" },
          ],
        }}
        loading={isLoading || isFetching}
        rowKey="id"
        checkable={true}
        data={data?.data}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        onSelectedKeysChange={onSelectedKeysChange}
        selectedKeys={selectedProducts.map((x) => x.id)}
        totalRows={data?.total_count}
        pagination={pagination}
        sort={sort}
        search={search}
        filter={filter}
        columns={columns}
        rowLink={(product) => `/products/${product.id}/usage`}
      />
    </div>
  );
}

export default UsageOverview;
